import { Button } from '@cibli/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { getInterviewUrl } from '@/lib/interview-url';

import DashboardLayout from '../../common/components/DashboardLayout';
import SelectCVDialog from '../../cv/components/SelectCVDialog';
import ConfirmApplyToJobDialog from '../components/ConfirmApplyToJobDialog';
import { CVFilterSidebar } from '../components/CVFilterSidebar';
import { JobDetailContent } from '../components/JobDetailContent';
import JobFilters from '../components/JobFilters';
import JobListColumn from '../components/JobListColumn';
import JobListEmptyState from '../components/JobListEmptyState';
import JobListErrorState from '../components/JobListErrorState';
import JobListLoadingState from '../components/JobListLoadingState';
import { FilteredJobsProvider, useFilteredJobsContext } from '../contexts/FilteredJobsContext';
import { useApplyToJob } from '../hooks/useApplyToJob';
import { useJobSelection } from '../hooks/useJobSelection';

interface JobListPageContentProps {
  selectedCvId: string | null;
  setSelectedCvId: (cvId: string | null) => void;
}

function JobListPageContent({ selectedCvId, setSelectedCvId }: JobListPageContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { jobs, pagination, error, handleRetry, hasAppliedToJob, refetchApplications } =
    useFilteredJobsContext();
  const { selectedJob, isClosing, selectJob, clearSelection } = useJobSelection();
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  const {
    applyToJob,
    isApplying,
    confirmApplyOpen,
    setConfirmApplyOpen,
    selectCVDialogOpen,
    setSelectCVDialogOpen,
    applicationResult,
  } = useApplyToJob({
    cvId: selectedCvId,
    requireCvSelection: true,
    onSuccess: refetchApplications,
  });

  const hasApplied = selectedJob ? hasAppliedToJob({ id: selectedJob.id }) : false;

  const handleToggleCv = (cvId: string) => {
    setSelectedCvId(selectedCvId === cvId ? null : cvId);
  };

  const handleApplyClick = () => {
    if (!selectedJob || hasApplied) return;

    if (!selectedCvId) {
      setPendingJobId(selectedJob.id);
    }

    void applyToJob(selectedJob, selectedCvId ?? undefined);
  };

  // Empty state: back to CV edit (direct edit when a CV is selected, CV list otherwise).
  const handleEditCv = () => {
    if (selectedCvId) {
      window.location.href = new URL(`/cv/${selectedCvId}/edit`, getInterviewUrl()).toString();
      return;
    }
    navigate('/');
  };

  const handleSelectCV = (cvId: string) => {
    setSelectedCvId(cvId);

    if (pendingJobId) {
      const pendingJob = jobs.find(j => j.id === pendingJobId);
      if (pendingJob) {
        void applyToJob(pendingJob, cvId);
      }
      setPendingJobId(null);
    }
  };

  return (
    <DashboardLayout title={t('dashboard.jobs.title')} subtitle={t('dashboard.jobs.subtitle')}>
      <div className="flex gap-6 h-full">
        {/* ====== CENTER: JOBS (always visible) ====== */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <JobFilters />

          {pagination.isLoading && jobs.length === 0 ? (
            <JobListLoadingState />
          ) : error ? (
            <JobListErrorState error={error} onRetry={handleRetry} />
          ) : jobs.length === 0 ? (
            <JobListEmptyState
              hasActiveFilters={pagination.hasActiveFilters}
              onEditCv={handleEditCv}
            />
          ) : (
            <div className="flex gap-6 flex-1 overflow-hidden min-h-0 relative">
              <div
                className={`transition-all duration-300 flex flex-col overflow-hidden min-h-0 ${
                  selectedJob && !isClosing
                    ? 'hidden md:flex-1 md:block md:min-w-0'
                    : 'flex-1 min-w-0'
                }`}
              >
                <JobListColumn selectedJob={selectedJob} onJobClick={selectJob} />
              </div>

              {/* Desktop: side panel, Mobile: full overlay */}
              <div
                className={`transition-all duration-300 flex flex-col min-h-0 relative ${
                  selectedJob && !isClosing
                    ? 'opacity-100'
                    : 'opacity-0 overflow-hidden pointer-events-none'
                } ${
                  selectedJob && !isClosing
                    ? 'md:flex-1 md:min-w-0 md:w-auto md:block'
                    : 'md:w-0 md:min-w-0 md:flex-[0] md:hidden'
                } ${
                  selectedJob && !isClosing
                    ? 'fixed inset-0 z-50 bg-background md:static md:inset-auto md:z-auto md:bg-transparent'
                    : 'hidden md:static md:inset-auto md:z-auto md:bg-transparent'
                }`}
              >
                {selectedJob && (
                  <>
                    <div className="absolute top-4 left-4 z-40 md:hidden">
                      <Button
                        variant="outline"
                        onClick={clearSelection}
                        aria-label={t('common.close')}
                      >
                        ← {t('common.back')}
                      </Button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-hidden md:h-full">
                      <div className="relative h-full overflow-y-auto pr-1 scrollbar-hide">
                        <JobDetailContent
                          job={selectedJob}
                          showCard={true}
                          isApplying={isApplying}
                          hasApplied={hasApplied}
                          handleApplyClick={handleApplyClick}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ====== RIGHT: CV FILTER SIDEBAR (desktop only) ====== */}
        <aside className="hidden lg:flex w-80 flex-shrink-0 flex-col overflow-hidden">
          <CVFilterSidebar activeCvId={selectedCvId} onToggleCv={handleToggleCv} />
        </aside>
      </div>

      <SelectCVDialog
        open={selectCVDialogOpen}
        onOpenChange={setSelectCVDialogOpen}
        onSelectCV={handleSelectCV}
        selectedCvId={selectedCvId}
      />
      <ConfirmApplyToJobDialog
        open={confirmApplyOpen}
        onOpenChange={setConfirmApplyOpen}
        applicationResult={applicationResult}
      />
    </DashboardLayout>
  );
}

export default function JobListPage() {
  const [selectedCvId, setSelectedCvId] = useState<string | null>(null);

  return (
    <FilteredJobsProvider cvId={selectedCvId}>
      <JobListPageContent selectedCvId={selectedCvId} setSelectedCvId={setSelectedCvId} />
    </FilteredJobsProvider>
  );
}
